import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hubRegions, enrichedHotels, BOOKING_LINK } from '@/data/romaniaHub';
import { MapPin, ArrowRight } from 'lucide-react';

// Approximate positions (%) of each region on a stylised Romania map.
const POSITIONS: Record<string, { x: number; y: number }> = {
  bucharest: { x: 58, y: 74 },
  'danube-delta': { x: 88, y: 58 },
  brasov: { x: 56, y: 55 },
  castles: { x: 50, y: 60 },
  carpathians: { x: 42, y: 48 },
  transylvania: { x: 40, y: 40 },
  sighisoara: { x: 52, y: 42 },
  viscri: { x: 58, y: 46 },
  maramures: { x: 38, y: 18 },
  bucovina: { x: 62, y: 18 },
};

const RomaniaMapSection: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(hubRegions[0].id);
  const region = hubRegions.find((r) => r.id === active)!;
  const hotels = enrichedHotels
    .filter((h) => region.hotelRegions.includes(h.region))
    .slice(0, 3);

  return (
    <section id="map" className="py-24 lg:py-32 bg-[#F5F1E8]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              Interactive Map
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6">
            Explore Every Destination
          </h2>
          <p className="font-sans text-sm text-[#666] max-w-2xl mx-auto leading-relaxed">
            Select a region on the map to reveal its overview, signature attractions and
            recommended luxury hotels.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12 items-start">
          {/* Stylised map */}
          <div className="lg:col-span-3">
            <div className="relative aspect-[4/3] bg-[#FAF3E4] border border-[#C9A961]/30 overflow-hidden">
              <div className="absolute inset-0 opacity-[0.35] bg-gradient-to-br from-[#C9A961]/20 via-transparent to-[#1A3B5C]/10" />
              {hubRegions.map((r) => {
                const pos = POSITIONS[r.id] ?? { x: 50, y: 50 };
                const isActive = r.id === active;
                return (
                  <button
                    key={r.id}
                    onClick={() => setActive(r.id)}
                    style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group"
                    aria-label={r.name}
                  >
                    <span
                      className={`block rounded-full transition-all duration-300 ${
                        isActive
                          ? 'w-5 h-5 bg-[#C9A961] ring-4 ring-[#C9A961]/30'
                          : 'w-3 h-3 bg-[#1A3B5C] group-hover:bg-[#C9A961]'
                      }`}
                    />
                    <span
                      className={`absolute left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap font-sans text-[9px] tracking-wider uppercase ${
                        isActive ? 'text-[#C9A961] font-medium' : 'text-[#1A3B5C]/70'
                      }`}
                    >
                      {r.name}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-center font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mt-6">
              Romania · 10 Curated Regions
            </p>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-2">
            <div className="relative aspect-[16/9] overflow-hidden mb-5">
              <img src={region.image} alt={region.imageAlt} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <h3 className="absolute bottom-4 left-5 font-serif text-2xl text-white font-light">{region.name}</h3>
            </div>
            <p className="font-sans text-xs text-[#555] leading-relaxed mb-5">{region.intro}</p>

            <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-2">Featured Attractions</p>
            <div className="flex flex-wrap gap-1.5 mb-6">
              {region.mustSee.map((a) => (
                <span key={a} className="font-sans text-[10px] text-[#8a7a55] bg-[#FAF3E4] px-2.5 py-1">{a}</span>
              ))}
            </div>

            <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3">Recommended Hotels</p>
            <div className="space-y-3 mb-6">
              {hotels.map((h) => (
                <div key={h.id} className="flex gap-3 group cursor-pointer" onClick={() => navigate(BOOKING_LINK)}>
                  <img src={h.image} alt={h.alt} className="w-16 h-14 object-cover flex-shrink-0" loading="lazy" />
                  <div className="min-w-0">
                    <p className="font-serif text-sm text-[#1A3B5C] group-hover:text-[#C9A961] transition-colors truncate">{h.name}</p>
                    <p className="flex items-center gap-1 font-sans text-[10px] text-[#999] truncate">
                      <MapPin className="w-3 h-3 text-[#C9A961]" /> {h.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate(BOOKING_LINK)}
              className="w-full font-sans text-[11px] tracking-[0.25em] uppercase px-6 py-3.5 bg-[#1A3B5C] text-white hover:bg-[#C9A961] transition-colors flex items-center justify-center gap-3"
            >
              Plan {region.name} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RomaniaMapSection;
